import {FC} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import DjangoMessages from 'components/DjangoMessages';
import FormActions from 'components/Form/FormActions';
import FormHeader from 'components/Form/FormHeader';
import FormResult from 'components/Form/FormResult';
import SubmitButton from 'components/Form/SubmitButton';
import NavigationTabs from 'components/Navigation/NavigationTabs';
import TooltipWrapper from 'components/Tooltip';
import {useBackend} from 'state/backend';
import {setUrl} from 'utils/request';
import {onApiError, onApiSuccess} from 'utils/request-axios/utils';
import OtherOptions from '../components/otherOptions';
import {RightsProvider} from '../components/RightsContext';
import RightsSections from '../components/RightsSection';
import UserLicense from '../components/userLicense';
import {createInitialValues} from '../helpers';
import {
    EmployeeRightsCreateFieldValues,
    EmployeeRightsEditBackendData,
    EmployeeRightsEditFieldValues,
} from '../types';
import usePostEditTeamMember from './services';

const EmployeeRightsEdit: FC = () => {
    const {t} = useTranslation('Team.Member.EmployeeRights');
    const {
        appPermissions: {canEditRights},
        data,
    } = useBackend<EmployeeRightsEditBackendData>();
    const methods = useForm<EmployeeRightsCreateFieldValues>({
        defaultValues: createInitialValues(data),
    });

    const {mutateAsync} = usePostEditTeamMember();

    const handleSubmit = (formData: EmployeeRightsEditFieldValues) => {
        mutateAsync({formData, mid: data.mid})
            .then(
                onApiSuccess({
                    onRedirect: (response) => {
                        if (response.name === 'employee_list_view') {
                            return setUrl(window.Urls.employee_list_view());
                        }
                        throw new Error('Unknown redirect');
                    },
                }),
            )
            .catch(onApiError(methods));
    };

    return (
        <>
            <DjangoMessages />
            <FormProvider {...methods}>
                <form onSubmit={methods.handleSubmit(handleSubmit)}>
                    <RightsProvider>
                        <FormHeader title={data.name} />
                        <NavigationTabs.TeamMember
                            className="my-4"
                            mid={data.mid}
                            {...data.navigationTabs}
                            selection="employeeRights"
                        />
                        <UserLicense />
                        <RightsSections />
                        <OtherOptions />
                        <FormResult className="my-2" />
                        <FormActions align="left">
                            <TooltipWrapper
                                message={canEditRights.failureReason}
                            >
                                <SubmitButton disabled={!canEditRights.allowed}>
                                    {t('save')}
                                </SubmitButton>
                            </TooltipWrapper>
                        </FormActions>
                    </RightsProvider>
                </form>
            </FormProvider>
        </>
    );
};

export default EmployeeRightsEdit;
